/* eslint-disable import/no-cycle */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from 'antd';
import { useLocation } from 'react-router-dom';
import useMRPFields from './mrpFields';
import useMRIMLFields from './mrimlFields';
import usePNOMLFields from './pnomlFields';
import useLastStepFields from './lastStepFields';
import useRCPFields from './rcpFields';
import useRCPHXFields from './rcphxFields';
import useRCGLSFields from './rcglsFields';
import useASSOFields from './assoFields';
import sharedFields from './sharedFields';
import usePrevoyanceFields from './prevoyanceFields';
import useMRIWKFields from './mriwkFields';
import useRCNGAFields from './rcngawkFields';

const useStepsByType = (form, purpose, currentStep = 0, type) => {
  const { t } = useTranslation();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const supplier = (params.get('sup') ?? '').trim();
  const assoType = params.get('assoT');
  const [terminationNonPayment, setTerminationNonPayment] = useState(false);

  const typeKey = type?.toLowerCase();
  const { lastStepFields } = useLastStepFields(form);

  const { mrpFields } = useMRPFields(form, supplier);
  const { rcpFields } = useRCPFields(form, supplier);
  const { rcphxFields } = useRCPHXFields(form, typeKey === 'rcphx');
  const { rcglsFields } = useRCGLSFields(form);
  const { rcngawkFields } = useRCNGAFields();
  const { assoFields } = useASSOFields({
    form,
    terminationNonPayment,
    setTerminationNonPayment
  });
  const { mrimlFields } = useMRIMLFields(form);
  const { pnomlFields } = usePNOMLFields(form);
  const { prevoyanceFields } = usePrevoyanceFields(form);
  const { mriwkFields, displayEstimationCard } = useMRIWKFields(
    form,
    purpose,
    currentStep
  );

  let steps = {};

  switch (typeKey) {
    case 'rcp':
      steps = {
        rcp: [
          {
            title: t('quotes.form.rcp.firstStep'),
            content: [
              ...rcpFields.filter((f) => f.step === 0),
              ...sharedFields({
                terminationNonPayment,
                setTerminationNonPayment
              })
            ],
            estimationCard: true
          },
          {
            title: t('quotes.form.rcp.secondStep'),
            subTitle: (
              <div
                style={{ marginBottom: 16 }}
                className="prerequisites-header"
              >
                <Typography.Title level={4} style={{ marginBottom: 0 }}>
                  {t('quotes.form.mrp.subtitle.step1.title')}
                </Typography.Title>
                <Typography.Text style={{ fontStyle: 'italic' }}>
                  {t('quotes.form.mrp.subtitle.step1.text')}
                </Typography.Text>
              </div>
            ),
            content: rcpFields.filter((f) => f.step === 1),
            preRequisites: true
          },
          {
            title: t('quotes.form.rcp.thirdStep'),
            content: lastStepFields
          }
        ]
      };
      break;

    case 'rcngawk':
      steps = {
        rcngawk: [
          {
            title: t('quotes.form.rcp.firstStep'),
            content: [
              ...rcngawkFields.filter((f) => f.step === 0),
              ...sharedFields({
                terminationNonPayment,
                setTerminationNonPayment
              }).filter(
                (f) =>
                  !['company_in_creation', 'company_former'].includes(f.name[0])
              )
            ],
            estimationCard: true
          },
          {
            title: t('quotes.form.rcp.secondStep'),
            subTitle: (
              <div
                style={{ marginBottom: 16 }}
                className="prerequisites-header"
              >
                <Typography.Title level={4} style={{ marginBottom: 0 }}>
                  {t('quotes.form.mrp.subtitle.step1.title')}
                </Typography.Title>
              </div>
            ),
            content: rcngawkFields.filter((f) => f.step === 1),
            preRequisites: true
          },
          {
            title: t('quotes.form.rcp.thirdStep'),
            content: lastStepFields
          }
        ]
      };
      break;

    case 'association':
      steps = {
        association: [
          ...(assoType === 'localRc'
            ? [
                {
                  title: t('quotes.form.asso.firstStep'),
                  content: assoFields.filter((f) => f.step === 0)
                },
                {
                  title: t('quotes.form.asso.secondStep'),
                  content: [
                    ...assoFields.filter((f) => f.step === 1),
                    ...sharedFields({
                      terminationNonPayment,
                      setTerminationNonPayment
                    })
                  ],
                  estimationCard: true
                },
                {
                  title: t('quotes.form.asso.thirdStep'),
                  subTitle: (
                    <div
                      style={{ marginBottom: 16 }}
                      className="prerequisites-header"
                    >
                      <Typography.Title level={4} style={{ marginBottom: 0 }}>
                        {t('quotes.form.asso.subtitle.step1.title')}
                      </Typography.Title>
                      <Typography.Text style={{ fontStyle: 'italic' }}>
                        {t('quotes.form.asso.subtitle.step1.text')}
                      </Typography.Text>
                    </div>
                  ),
                  content: assoFields.filter((f) => f.step === 2),
                  preRequisites: true
                },
                {
                  title: t('quotes.form.asso.fourthStep'),
                  content: lastStepFields
                }
              ]
            : [
                {
                  title: t('quotes.form.asso.secondStep'),
                  content: [
                    ...assoFields.filter((f) => f.step === 0),
                    ...sharedFields({
                      terminationNonPayment,
                      setTerminationNonPayment
                    })
                  ],
                  estimationCard: true
                },
                {
                  title: t('quotes.form.rcp.secondStep'),
                  subTitle: (
                    <div
                      style={{ marginBottom: 16 }}
                      className="prerequisites-header"
                    >
                      <Typography.Title level={4} style={{ marginBottom: 0 }}>
                        {t('quotes.form.mrp.subtitle.step1.title')}
                      </Typography.Title>
                      <Typography.Text style={{ fontStyle: 'italic' }}>
                        {t('quotes.form.mrp.subtitle.step1.text')}
                      </Typography.Text>
                    </div>
                  ),
                  content: assoFields.filter((f) => f.step === 1),
                  preRequisites: true
                },
                {
                  title: t('quotes.form.rcp.thirdStep'),
                  content: lastStepFields
                }
              ])
        ]
      };
      break;

    case 'mrp':
      steps = {
        mrp: [
          {
            title: t('quotes.form.mrp.firstStep'),
            content: mrpFields.filter((f) => f.step === 0)
          },
          {
            title: t('quotes.form.mrp.secondStep'),
            content: [
              ...mrpFields.filter((f) => f.step === 1),
              ...sharedFields({
                terminationNonPayment,
                setTerminationNonPayment
              })
            ],
            estimationCard: true
          },
          {
            title: t('quotes.form.mrp.thirdStep'),
            subTitle: (
              <div
                style={{ marginBottom: 16 }}
                className="prerequisites-header"
              >
                <Typography.Title level={4} style={{ marginBottom: 0 }}>
                  {t('quotes.form.mrp.subtitle.step1.title')}
                </Typography.Title>
                <Typography.Text style={{ fontStyle: 'italic' }}>
                  {t('quotes.form.mrp.subtitle.step1.text')}
                </Typography.Text>
              </div>
            ),
            content: mrpFields.filter((f) => f.step === 2),
            preRequisites: true
          },
          {
            title: t('quotes.form.mrp.fourthStep'),
            content: lastStepFields
          }
        ]
      };
      break;

    case 'mriml':
      steps = {
        mriml: [
          {
            title: t('quotes.form.mri.secondStep'),
            content: [
              ...mrimlFields.filter((f) => f.step === 0),
              ...sharedFields({
                terminationNonPayment,
                setTerminationNonPayment
              })
            ],
            estimationCard: true
          },
          {
            title: t('quotes.form.mri.firstStep'),
            content: mrimlFields.filter((f) => f.step === 1),
            preRequisites: true,
            subTitle: (
              <div
                style={{ marginBottom: 16 }}
                className="prerequisites-header"
              >
                <Typography.Title level={4} style={{ marginBottom: 0 }}>
                  {t('quotes.form.mri.subtitle.step1.title')}
                </Typography.Title>
                <Typography.Text style={{ fontStyle: 'italic' }}>
                  {t('quotes.form.mri.subtitle.step1.text')}
                </Typography.Text>
              </div>
            )
          },
          {
            title: t('quotes.form.mri.thirdStep'),
            content: lastStepFields,
            estimationCard: true
          }
        ]
      };
      break;

    case 'pnoml':
      steps = {
        pnoml: [
          {
            title: t('quotes.form.mri.secondStep'),
            content: [
              ...pnomlFields.filter((f) => f.step === 0),
              ...sharedFields({
                terminationNonPayment,
                setTerminationNonPayment
              })
            ],
            estimationCard: true
          },
          {
            title: t('quotes.form.mri.firstStep'),
            content: pnomlFields.filter((f) => f.step === 1),
            preRequisites: true,
            subTitle: (
              <div
                style={{ marginBottom: 16 }}
                className="prerequisites-header"
              >
                <Typography.Title level={4} style={{ marginBottom: 0 }}>
                  {t('quotes.form.mri.subtitle.step1.title')}
                </Typography.Title>
                <Typography.Text style={{ fontStyle: 'italic' }}>
                  {t('quotes.form.mri.subtitle.step1.text')}
                </Typography.Text>
              </div>
            )
          },
          {
            title: t('quotes.form.mri.thirdStep'),
            content: lastStepFields,
            estimationCard: true
          }
        ]
      };
      break;

    case 'prevoyance':
      steps = {
        prevoyance: [
          {
            title: t('quotes.form.prevoyance.firstStep'),
            content: [
              ...prevoyanceFields.filter((f) => f.step === 0),
              ...sharedFields({
                terminationNonPayment,
                setTerminationNonPayment
              }).filter(
                (f) =>
                  !['company_in_creation', 'company_former'].includes(f.name[0])
              )
            ],
            estimationCard: true
          },
          {
            title: t('quotes.form.prevoyance.secondStep'),
            content: [
              ...prevoyanceFields.filter((f) => f.step === 1),
              ...sharedFields({
                terminationNonPayment,
                setTerminationNonPayment
              }).filter(
                (f) =>
                  ![
                    'company_in_creation',
                    'company_former',
                    'payment_type',
                    'commission_fee',
                    'broker_fee',
                    'contribution_adjustment',
                    'paperwork_fee',
                    'paperwork_fee_payment',
                    'sworn_statement'
                  ].includes(f.name[0])
              )
            ],
            estimationCard: true
          },
          {
            title: t('quotes.form.prevoyance.thirdStep'),
            content: lastStepFields
          }
        ]
      };
      break;

    case 'rcphx':
      steps = {
        rcphx: [
          {
            title: t('quotes.form.rcp.firstStep'),
            content: [
              ...rcphxFields.filter((f) => f.step === 0),
              ...sharedFields(
                {
                  terminationNonPayment,
                  setTerminationNonPayment
                },
                ['company_in_creation']
              )
            ],
            estimationCard: true
          },
          {
            title: t('quotes.form.rcp.secondStep'),
            subTitle: (
              <div
                style={{ marginBottom: 16 }}
                className="prerequisites-header"
              >
                <Typography.Title level={4} style={{ marginBottom: 0 }}>
                  {t('quotes.form.mrp.subtitle.step1.title')}
                </Typography.Title>
                <Typography.Text style={{ fontStyle: 'italic' }}>
                  {t('quotes.form.mrp.subtitle.step1.text')}
                </Typography.Text>
              </div>
            ),
            content: rcphxFields.filter((f) => f.step === 1),
            preRequisites: true
          },
          {
            title: t('quotes.form.rcp.thirdStep'),
            content: lastStepFields
          }
        ]
      };
      break;

    case 'rcgls':
      steps = {
        rcgls: [
          {
            title: t('quotes.form.rcp.firstStep'),
            content: [
              ...rcglsFields.filter((f) => f.step === 0),
              ...sharedFields(
                {
                  terminationNonPayment,
                  setTerminationNonPayment
                },
                ['company_in_creation']
              )
            ],
            estimationCard: true
          },
          {
            title: t('quotes.form.rcp.secondStep'),
            subTitle: (
              <div
                style={{ marginBottom: 16 }}
                className="prerequisites-header"
              >
                <Typography.Title level={4} style={{ marginBottom: 0 }}>
                  {t('quotes.form.mrp.subtitle.step1.title')}
                </Typography.Title>
                <Typography.Text style={{ fontStyle: 'italic' }}>
                  {t('quotes.form.mrp.subtitle.step1.text')}
                </Typography.Text>
              </div>
            ),
            content: rcglsFields.filter((f) => f.step === 1),
            preRequisites: true
          },
          {
            title: t('quotes.form.rcp.thirdStep'),
            content: lastStepFields
          }
        ]
      };
      break;

    case 'mriwk':
      steps = {
        mriwk: [
          {
            title: t('quotes.form.mriwk.firstStep'),
            content: [
              ...mriwkFields.filter((f) => f.step === 0),
              ...sharedFields(
                {
                  terminationNonPayment,
                  setTerminationNonPayment
                },
                [
                  'payment_type',
                  'company_in_creation',
                  'contribution_adjustment'
                ]
              )
            ],
            estimationCard: displayEstimationCard
          },
          {
            title: t('quotes.form.mriwk.secondStep'),
            subTitle: (
              <div
                style={{ marginBottom: 16 }}
                className="prerequisites-header"
              >
                <Typography.Title level={4} style={{ marginBottom: 0 }}>
                  {t('quotes.form.mriwk.subtitle.prerequisites.title')}
                </Typography.Title>
                <Typography.Text style={{ fontStyle: 'italic' }}>
                  {t('quotes.form.mriwk.subtitle.prerequisites.text')}
                </Typography.Text>
              </div>
            ),
            content: mriwkFields.filter((f) => f.step === 1),
            preRequisites: true
          },
          {
            title: t('quotes.form.mriwk.fourthStep'),
            content: lastStepFields
          }
        ]
      };
      break;

    default:
      steps = {};
  }

  return { steps };
};

export default useStepsByType;
